<template>
  <div id="branchmap" class="w-full h-full" data-test-id="branchGmapWrapper" data-fs="branchGmapWrapper" />
</template>

<script setup lang="ts">
import { Branch } from "mkm-types";
import locationPin from "/icons/location-pin.svg";
import bluePin from "/icons/pin-blue.svg";

import { MarkerClusterer, SuperClusterAlgorithm, Renderer } from "@googlemaps/markerclusterer";
import { Loader } from "@googlemaps/js-api-loader";

const emit = defineEmits<{
  (event: "select:branch", value: Branch): void;
}>();

const { GOOGLE_MAPS_API_KEY } = useRuntimeConfig().public;
const { allFetchedBranches } = useBranches();

const props = defineProps(["currentLocation", "branches", "selectPin", "searchNearest"]);

const initMap = async () => {
  if (!allFetchedBranches.value) return;

  const { Map } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
  const { Marker } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;

  const map = new Map(document.querySelector("#branchmap") as HTMLElement, {
    zoom: props.searchNearest ? 9 : 6.2,
    center: props.currentLocation,
    mapId: "branchmap",
  });

  const markers = allFetchedBranches.value.map((branch) => {
    const marker = new Marker({
      map: map,
      position: { lat: Number(branch.coordinates.lat), lng: Number(branch.coordinates.lng) },
      title: branch.name,
      icon: locationPin,
    });

    marker.addListener("click", () => {
      emit("select:branch", branch);
    });

    return marker;
  });

  map.setOptions({ fullscreenControl: false, scrollwheel: false, zoomControl: true });

  const renderer: Renderer = {
    render({ count, position }) {
      return new google.maps.Marker({
        label: {
          text: String(count),
          color: "white",
          fontSize: "24px",
          fontWeight: "900",
          className: "MarkerClusterer_name",
        },
        position,
        icon: bluePin,
      });
    },
  };

  const algorithm = new SuperClusterAlgorithm({ radius: 95 });

  new MarkerClusterer({ markers, map, renderer, algorithm });
};

onMounted(() => {
  const loader = new Loader({
    apiKey: String(GOOGLE_MAPS_API_KEY),
  });

  loader
    .importLibrary("places")
    .then(() => {
      new google.maps.Geocoder();
    })
    .catch((error) => {
      console.error("Error occurred while loading Google Maps", error);
    });

  nextTick(() => {
    if (typeof google === "object" && typeof google.maps === "object") {
      initMap();
    }
  });
});

watch(
  () => props.currentLocation,
  () => {
    initMap();
  },
);
</script>
